import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState } from 'react';
import { Check, ChevronsUpDown, Film } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface SearchOption { value: string; label: string; image?: string | null; detail?: string; }
export function SearchSelect({ value, options, onChange, label, placeholder = translateInterfaceText("Escolha uma opção") }: {
  value: string; options: SearchOption[]; onChange: (value: string) => void; label: string; placeholder?: string;
}) {
  useLanguage();
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-label={label} aria-expanded={open}
          className="h-auto min-h-10 w-full justify-between gap-2 rounded-[10px] border-[#D9E0EA] px-2.5 py-2 text-left text-[13px] font-normal hover:bg-[#F4F6FA] hover:text-[#111827]">
          {selected?.image && <img src={selected.image} alt="" className="h-8 w-7 shrink-0 rounded object-cover" />}
          <span className="min-w-0 flex-1 truncate">{selected?.label || placeholder}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-[#6B7280]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[220px] p-0">
        <Command>
          <CommandInput placeholder={translateInterfaceText("Buscar...")} aria-label={translateInterfaceText("Buscar em {value0}", { value0: (label) })} />
          <CommandList>
            <CommandEmpty>{translateInterfaceText("Nenhum resultado.")}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem key={option.value} value={`${option.value} ${option.label}`} onSelect={() => { onChange(option.value); setOpen(false); }}
                  className={cn('gap-2 rounded-lg py-2 aria-selected:bg-[#F4F6FA]', value === option.value && 'bg-[#EEF4FF] text-[#0057FF] aria-selected:bg-[#E0EAFF]')}>
                  {option.image ? <img src={option.image} alt="" className="h-10 w-8 shrink-0 rounded object-cover" /> : option.detail ? <Film className="h-5 w-5 shrink-0" /> : null}
                  <span className="min-w-0 flex-1"><span className="block truncate">{option.label}</span>{option.detail && <span className="block text-xs text-[#6B7280]">{option.detail}</span>}</span>
                  {value === option.value && <Check className="h-4 w-4 shrink-0 text-[#0057FF]" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
